import React, { useState, useEffect, useRef } from "react";
import { Button, Icon, Transition } from "semantic-ui-react";
import MenuHorizontal from "../../layouts/MenuHorizontal";
import MenuVertical from "../../layouts/MenuVertical";
import CondominioVencido from '../admin/CondominioVencido';
import Erro from "../../layouts/mensagens/Erro";
import * as FormValidator from "../../../utils/formValidator";
import api from "../../../api";
import { prepararArquivoImportacao, conferirLinhas, COLUNAS_IMPORTACAO } from "../../../utils/importacaoArquivo";
import ResultadoImportacao from "./ResultadoImportacao";

const initialStepState = {
  step1: { active: true, completed: false },
  step2: { active: false, completed: false },
};

const initialValidationResultState = {
  data: [],
  errors: [],
  rowCount: 0,
  invalidRowCount: 0,
  validRowCount: 0,
};

const MODELO_XLS = '/modelos/modelo-importacao.xlsx';
const MODELO_CSV = '/modelos/modelo-importacao.csv';

// Traduz a falha da chamada em uma mensagem útil (antes tudo virava "erro inesperado").
const descreverErro = e => {
  const status = e && e.status;
  const mensagem = e && e.data && (e.data.message || e.data.error);

  if (e && (e.problem === 'NETWORK_ERROR' || e.problem === 'CONNECTION_ERROR')) {
    return {
      titulo: 'Não foi possível conectar ao serviço de importação',
      detalhe: 'O serviço não respondeu. Verifique sua conexão e tente novamente; se continuar, o serviço pode estar temporariamente indisponível.',
    };
  }
  if (e && e.problem === 'TIMEOUT_ERROR') {
    return { titulo: 'A importação demorou demais', detalhe: 'O arquivo é grande ou o serviço está lento. Tente de novo ou divida o arquivo em partes menores.' };
  }
  if (status === 401 || status === 403) {
    return { titulo: 'Sua sessão expirou', detalhe: 'Entre novamente no sistema e repita a importação.' };
  }
  if (status === 413) {
    return { titulo: 'Arquivo grande demais', detalhe: `O limite é de ${process.env.REACT_APP_LIMIT_UPLOAD}mb. Divida o arquivo em partes menores.` };
  }
  if (mensagem && String(mensagem).includes('duplicate key')) {
    return { titulo: 'Há e-mails ou documentos repetidos', detalhe: 'Existem registros com o e-mail duplicado. Verifique o arquivo importado e remova as repetições.' };
  }
  if (mensagem) {
    return { titulo: 'A importação não foi concluída', detalhe: String(mensagem) };
  }
  return {
    titulo: 'A importação não foi concluída',
    detalhe: `O serviço respondeu com erro${status ? ` (código ${status})` : ''}. Tente novamente e, se persistir, fale com o suporte.`,
  };
};

export default function ImportacaoCSV({ match }) {
  const [validationResult, setValidationResult] = useState(initialValidationResultState);
  const [globalError, setGlobalError] = useState(null);
  const [steps, setSteps] = useState(initialStepState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [infoArquivo, setInfoArquivo] = useState(null);
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [invalidarHash, setInvalidarHash] = useState(false);
  const [condominio, setCondominio] = useState({});
  const [arrastando, setArrastando] = useState(false);
  const inputFileRef = useRef();

  useEffect(() => {
    const init = async () => {
      try {
        setCondominio(await api.condominio.findSimpleById(match.params.condominioId));
      } catch(e) {
        //
      }
    };
    init();
  }, [match.params.condominioId]);

  async function onSubmit(e) {
    e.preventDefault();
    const errors = validate();
    setErrors(errors);

    if (FormValidator.objIsEmpty(errors)) {
      setLoading(true);
      setSteps({
        ...initialStepState,
        step1: { active: false, completed: true },
        step2: { active: true, completed: false },
      });

      const formData = new FormData();
      formData.append("file", file);
      formData.append("condominio", condominio._id);
      formData.append("enviarEmail", enviarEmail);
      formData.append("invalidarHash", invalidarHash);

      try {
        const response = await api.importacao.importar(formData);
        setValidationResult(response);
        setLoading(false);
        setGlobalError(null);
      } catch (e) {
        setLoading(false);
        setGlobalError(descreverErro(e));
      }
    }
  }

  function startAgain() {
    setLoading(false);
    setSteps(initialStepState);
    setValidationResult(initialValidationResultState);
    setGlobalError(null);
    setFile(null);
    setInfoArquivo(null);
    clearInputFile();
  }

  function validate() {
    const errors = {};

    if (!file) {
      errors.mensagem = "Adicione um arquivo";
    }

    return errors;
  }

  function clearInputFile() {
    if (inputFileRef.current && inputFileRef.current.inputRef.current) {
      inputFileRef.current.inputRef.current.value = null;
    }
  }

  async function processarArquivo(currentFile) {
    if (!currentFile) return;
    if (!FormValidator.sizeLimit(currentFile, process.env.REACT_APP_LIMIT_UPLOAD)) {
      setErrors({ mensagem: `Permitido arquivos de até ${process.env.REACT_APP_LIMIT_UPLOAD}mb` });
      setFile(null);
      setInfoArquivo(null);
      clearInputFile();
      return;
    }

    try {
      // XLS/XLSX são convertidos para o CSV esperado pelo serviço de importação
      const preparado = await prepararArquivoImportacao(currentFile);
      setFile(preparado.file);
      setInfoArquivo({ nome: preparado.original, linhas: preparado.linhas, dados: preparado.dados, avisos: conferirLinhas(preparado.dados) });
      setErrors({});
    } catch (err) {
      setErrors({ mensagem: err.message });
      setFile(null);
      setInfoArquivo(null);
      clearInputFile();
    }
  }

  const onChangeFile = e => processarArquivo(e.target.files && e.target.files[0]);

  function onDrop(e) {
    e.preventDefault();
    setArrastando(false);
    processarArquivo(e.dataTransfer.files && e.dataTransfer.files[0]);
  }

  function removerArquivo() {
    setFile(null);
    setInfoArquivo(null);
    setErrors({});
    clearInputFile();
  }

  function onChangeEnviarEmail() {
    setEnviarEmail(!enviarEmail);
  }

  function onChangeInvalidarHash(e) {
    setInvalidarHash(!invalidarHash);
  }

  const COLUNAS_AJUDA = [
    ['ativo', 's (sim) ou n (não)', 's'],
    ['bloco', 'Bloco ou torre (opcional)', 'A'],
    ['numero', 'Número da unidade', '101'],
    ['fracao', 'Fração ideal (ex.: 0.05)', '0.05'],
    ['nome', 'Nome do responsável', 'Ana Souza'],
    ['documento', 'CPF ou CNPJ, só números', '11144477735'],
    ['email', 'E-mail do responsável', 'ana@exemplo.com.br'],
    ['celular', 'Celular com DDD', '31999990001'],
  ];

  return (
    <div>
      <MenuHorizontal />
      <div className="corpo-dashboard">
        <MenuVertical condominio={condominio} />
        <CondominioVencido condominio={condominio}>
          <div className="va-imp-pagina">
          <header className="va-un-cabecalho">
            <div>
              <h1>Importação de arquivo</h1>
              <p>Cadastre ou atualize várias unidades votantes de uma só vez, enviando uma planilha XLS, XLSX ou CSV.</p>
            </div>
          </header>

          <ol className="va-imp-passos" aria-label="Etapas da importação">
            <li className={steps.step1.active ? 'ativo' : 'feito'}><span>{steps.step1.completed ? <Icon name="check" /> : 1}</span> Preparar e enviar o arquivo</li>
            <li className={steps.step2.active ? 'ativo' : ''}><span>2</span> Conferir o resultado</li>
          </ol>

          {steps.step1.active && (
            <Transition duration={{ show: 600, hide: 0 }} animation="fade up" transitionOnMount={true} visible={steps.step1.active}>
              <form onSubmit={onSubmit} autoComplete="off" className="va-imp">
                <section className="va-imp-bloco">
                  <h3><b>1</b> Baixe o modelo</h3>
                  <p>Comece pelo modelo: ele já vem com o cabeçalho certo e uma linha de exemplo. Apague o exemplo e preencha com as suas unidades.</p>
                  <div className="va-modelo-botoes">
                    <a className="ui button btn-ui-1" href={MODELO_XLS} download="modelo-importacao.xlsx">
                      <Icon name="file excel outline" /> Baixar modelo XLSX (recomendado)
                    </a>
                    <a className="ui basic button" href={MODELO_CSV} download="modelo-importacao.csv">
                      <Icon name="file alternate outline" /> Baixar modelo CSV
                    </a>
                  </div>
                  <details className="va-imp-colunas">
                    <summary>Ver o significado de cada coluna</summary>
                    <table className="va-modelo-tabela">
                      <thead><tr><th>Coluna</th><th>O que preencher</th><th>Exemplo</th></tr></thead>
                      <tbody>
                        {COLUNAS_AJUDA.map(([col, desc, ex]) => (
                          <tr key={col}><td><b>{col}</b></td><td>{desc}</td><td>{ex}</td></tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="va-imp-dica"><Icon name="lightbulb outline" /> Formate <b>documento</b> e <b>celular</b> como texto para não perder zeros à esquerda. A aba "Instruções" do modelo XLSX repete essas explicações.</p>
                  </details>
                </section>

                <section className="va-imp-bloco">
                  <h3><b>2</b> Envie o arquivo preenchido</h3>
                  <label
                    htmlFor="arquivo"
                    className={`va-imp-zona ${arrastando ? 'arrastando' : ''} ${file ? 'com-arquivo' : ''} ${errors.mensagem ? 'erro' : ''}`}
                    onDragOver={e => { e.preventDefault(); setArrastando(true); }}
                    onDragLeave={() => setArrastando(false)}
                    onDrop={onDrop}
                  >
                    <Icon name={file ? 'file excel outline' : 'cloud upload'} size="big" />
                    {file ? (
                      <span className="va-imp-zona-texto">
                        <b>{infoArquivo ? infoArquivo.nome : file.name}</b>
                        {infoArquivo && <small>{infoArquivo.linhas} linha{infoArquivo.linhas !== 1 && 's'} encontrada{infoArquivo.linhas !== 1 && 's'} · pronto para importar</small>}
                      </span>
                    ) : (
                      <span className="va-imp-zona-texto">
                        <b>Arraste o arquivo aqui ou clique para selecionar</b>
                        <small>XLS, XLSX ou CSV · até {process.env.REACT_APP_LIMIT_UPLOAD}mb</small>
                      </span>
                    )}
                    <input
                      type="file"
                      id="arquivo"
                      name="arquivo"
                      onChange={onChangeFile}
                      accept=".xls,.xlsx,.csv"
                      ref={inputFileRef}
                      className="va-imp-input"
                    />
                  </label>
                  {file && (
                    <button type="button" className="va-imp-trocar" onClick={removerArquivo}>
                      <Icon name="trash alternate outline" /> Remover e escolher outro arquivo
                    </button>
                  )}
                  <Erro text={errors.mensagem} />
                  {infoArquivo && infoArquivo.dados && (
                    <div className="va-imp-previa">
                      <div className={`va-imp-resumo ${infoArquivo.avisos.length ? 'atencao' : 'ok'}`}>
                        <Icon name={infoArquivo.avisos.length ? 'warning sign' : 'check circle'} />
                        {infoArquivo.avisos.length
                          ? <span><b>{infoArquivo.avisos.length} linha{infoArquivo.avisos.length !== 1 && 's'} com possível problema</b> de {infoArquivo.linhas}. Confira antes de importar — o serviço rejeita apenas as linhas inválidas.</span>
                          : <span><b>Tudo certo na conferência:</b> {infoArquivo.linhas} linha{infoArquivo.linhas !== 1 && 's'} sem problemas aparentes.</span>}
                      </div>
                      {infoArquivo.avisos.length > 0 && (
                        <ul className="va-imp-avisos">
                          {infoArquivo.avisos.slice(0, 5).map(a => (
                            <li key={a.linha}><b>Linha {a.linha}:</b> {a.problemas.join('; ')}</li>
                          ))}
                          {infoArquivo.avisos.length > 5 && <li>... e mais {infoArquivo.avisos.length - 5} linha(s).</li>}
                        </ul>
                      )}
                      <details className="va-imp-colunas">
                        <summary>Pré-visualizar as primeiras linhas</summary>
                        <table className="va-modelo-tabela">
                          <thead><tr><th>#</th>{COLUNAS_IMPORTACAO.map(c => <th key={c}>{c}</th>)}</tr></thead>
                          <tbody>
                            {infoArquivo.dados.slice(0, 5).map((l, i) => (
                              <tr key={i} className={infoArquivo.avisos.some(a => a.linha === i + 2) ? 'aviso' : ''}>
                                <td>{i + 2}</td>{l.map((c, j) => <td key={j}>{c}</td>)}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </details>
                    </div>
                  )}
                </section>

                <section className="va-imp-bloco">
                  <h3><b>3</b> Escolha o que fazer após importar</h3>
                  <div className="va-imp-opcoes">
                    <label className={`va-imp-opcao ${enviarEmail ? 'ativa' : ''}`}>
                      <input type="checkbox" checked={enviarEmail} onChange={onChangeEnviarEmail} />
                      <span className="va-imp-chave" aria-hidden="true" />
                      <span>
                        <b>Enviar e-mail com o token de acesso</b>
                        <small>Cada responsável recebe o e-mail para entrar no aplicativo de votação.</small>
                      </span>
                    </label>
                    <label className={`va-imp-opcao ${invalidarHash ? 'ativa' : ''}`}>
                      <input type="checkbox" checked={invalidarHash} onChange={onChangeInvalidarHash} />
                      <span className="va-imp-chave" aria-hidden="true" />
                      <span>
                        <b>Gerar novo token para as unidades atualizadas</b>
                        <small>Invalida o token anterior das unidades que já existiam. Deixe desligado para mantê-lo.</small>
                      </span>
                    </label>
                  </div>
                  {!enviarEmail && (
                    <p className="va-imp-dica"><Icon name="info circle" /> Você também pode enviar o token depois, em "Unidades votantes" → "Enviar token para todas".</p>
                  )}
                </section>

                <div className="va-imp-acoes">
                  <Button type="submit" loading={loading} disabled={loading || !file} className="btn-ui-1" size="large" icon labelPosition="right">
                    Importar {infoArquivo ? `${infoArquivo.linhas} linha${infoArquivo.linhas !== 1 ? 's' : ''}` : 'arquivo'}
                    <Icon name="right arrow" />
                  </Button>
                  {!file && <span className="va-imp-aviso">Selecione um arquivo no passo 2 para continuar.</span>}
                </div>
              </form>
            </Transition>
          )}

          {steps.step2.active && (
            <Transition animation="fade up" duration={{ show: 600, hide: 0 }} transitionOnMount={true} visible={steps.step2.active}>
              <div>
                <ResultadoImportacao
                  resultado={validationResult}
                  erroGlobal={globalError}
                  carregando={loading}
                  condominioId={condominio._id}
                  enviouEmail={enviarEmail}
                  novoToken={invalidarHash}
                  onNova={startAgain}
                  onTentarNovamente={onSubmit}
                />
              </div>
            </Transition>
          )}
          </div>
        </CondominioVencido>
      </div>
    </div>
  );
}
